// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@eisinc/App/Components/Common/XngZone";
import Skeleton from "@eisinc/App/Components/Skeleton/Skeleton";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// <XNG-Changes>
const enum fields {
    showFilterLink = "showFilterLink",
    showControls = "showControls",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showFilterLink]: boolean;
        [fields.showControls]: boolean;
    };
}

// </XNG-Changes>

const mapStateToProps = (state: CustomAppState) => {
    // <XNG-Changes>
    const productsDataView = getProductListDataView(state);
    const hasProducts =
        productsDataView.value &&
        (productsDataView.value.length !== 0 || productsDataView.pagination?.totalItemCount !== 0);
    // </XNG-Changes>

    return {
        view: state.pages.productList.view,
        // XNG-Changes
        hasProducts,
        isLoading: state.pages.productList.isLoading,
        isTabletScreen: state.xCommon.isTabletScreen,
    };
};

// XNG-Changes: add mapDispatchToProps return type
type Props = OwnProps & ReturnType<typeof mapStateToProps> & WidgetProps;

export interface ProductListControlsContainerStyles {
    container?: GridContainerProps;
    countGridItem?: GridItemProps;
    countTableViewGridItem?: GridItemProps;
    sortGridItem?: GridItemProps;
    sortTableViewGridItem?: GridItemProps;
    filterGridItem?: GridItemProps;
    filterTableViewGridItem?: GridItemProps;
    viewSelectGridItem?: GridItemProps;
    viewSelectTableViewGridItem?: GridItemProps;
    // XNG-Changes
    controlsWrapper?: InjectableCss;
    viewSelectAndPaginationWrapper?: InjectableCss;
}

export const controlsContainerStyles: ProductListControlsContainerStyles = {
    container: {
        gap: 0,
    },
    countGridItem: {
        width: [12, 12, 2, 5, 6],
    },
    countTableViewGridItem: {
        width: [12, 12, 1, 1, 1],
    },
    sortGridItem: {
        width: [12, 12, 5, 4, 4],
    },
    sortTableViewGridItem: {
        width: [12, 12, 5, 4, 3],
    },
    filterGridItem: {
        width: 0,
    },
    filterTableViewGridItem: {
        width: [0, 0, 2, 2, 2],
    },
    viewSelectGridItem: {
        width: [0, 0, 5, 3, 2],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    viewSelectTableViewGridItem: {
        width: [0, 0, 4, 5, 6],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
};

const styles = controlsContainerStyles;

const ProductListControlsContainer: FC<Props> = ({ id, view, hasProducts, isLoading, isTabletScreen }) => {
    // <XNG-Changes>
    if (isLoading) {
        return (
            <StyledWrapper {...styles.controlsWrapper}>
                <Skeleton height="26px" {...styles.container} />
            </StyledWrapper>
        );
    }

    // </XNG-Changes>

    return (
        <>
            {/* XNG-Changes: Change Structure */}

            {hasProducts && (
                <StyledWrapper {...styles.controlsWrapper}>
                    <XngZone contentId={id} zoneName="Content00" hint="Product List Sort Select" />
                    <StyledWrapper {...styles.viewSelectAndPaginationWrapper}>
                        {!isTabletScreen && (
                            <XngZone contentId={id} zoneName="Content02" hint="Product List View Select" />
                        )}
                        <XngZone contentId={id} zoneName="Content01" hint="Product List Pagination" />
                    </StyledWrapper>
                </StyledWrapper>
            )}

            {/* <GridContainer {...styles.container}>
                <GridItem {...(view === "Table" ? styles.countTableViewGridItem : styles.countGridItem)}>
                    <Zone contentId={id} zoneName="Content00" />
                </GridItem>
                <GridItem {...(view === "Table" ? styles.sortTableViewGridItem : styles.sortGridItem)}>
                    <Zone contentId={id} zoneName="Content01" />
                </GridItem>
                <GridItem {...(view === "Table" ? styles.filterTableViewGridItem : styles.filterGridItem)}>
                    <Zone contentId={id} zoneName="Content02" />
                </GridItem>
                <GridItem {...(view === "Table" ? styles.viewSelectTableViewGridItem : styles.viewSelectGridItem)}>
                    <Zone contentId={id} zoneName="Content03" />
                </GridItem>
            </GridContainer> */}
        </>
    );
};

const widgetModule: WidgetModule = {
    // XNG-Changes: add mapDispatchToProps
    component: connect(mapStateToProps)(ProductListControlsContainer),
    definition: {
        group: "Product List",
        displayName: "List Controls Container",
        // XNG-Changes
        allowedContexts: ["ProductListPage", "CategoryDetailsPage"],
    },
};

export default widgetModule;
